/**
 * Projects Routes
 */

import type { RouteObject } from "react-router-dom";
import { useParams } from "react-router-dom";
import { ProtectedRoute } from "@/components/layout/protected-route";
import { useAuthStore } from "@/features/auth/store/auth.store";
import { BOQDetail } from "./components/boq/boq-detail";
import { BOQForm } from "./components/boq/boq-form";
import { BoqPrintPage } from "./components/boq/boq-print";
import { HoProjectDetail } from "./components/homeowner";
import { CreateProjectPage } from "./pages/create-project";
import { PaymentReturnPage } from "./pages/payment-return";
import { ProjectDetailPage } from "./pages/project-detail";
import { ProjectsPage } from "./pages/projects";
import { ProposalsPage } from "./pages/proposals";
import { RequestsPage } from "./pages/requests";

function ProjectDetailSelector() {
	const { id } = useParams<{ id: string }>();
	const { user } = useAuthStore();
	if (!id) return null;

	if (user?.userType === "homeowner") {
		return <HoProjectDetail projectId={id} />;
	}
	return <ProjectDetailPage />;
}

export const projectsRoutes: RouteObject[] = [
	{
		path: "/projects",
		element: (
			<ProtectedRoute requireUserType>
				<ProjectsPage />
			</ProtectedRoute>
		),
	},
	{
		path: "/projects/new",
		element: (
			<ProtectedRoute requireUserType="homeowner">
				<CreateProjectPage />
			</ProtectedRoute>
		),
	},
	{
		path: "/payments/return",
		element: (
			<ProtectedRoute requireUserType>
				<PaymentReturnPage />
			</ProtectedRoute>
		),
	},
	{
		// Where a provider redirect lands: the backend appends the project id
		// to the configured return URL (`buildPaymentReturnUrl`).
		path: "/payments/return/:projectId",
		element: (
			<ProtectedRoute requireUserType>
				<PaymentReturnPage />
			</ProtectedRoute>
		),
	},
	{
		path: "/projects/:id",
		element: (
			<ProtectedRoute requireUserType>
				<ProjectDetailSelector />
			</ProtectedRoute>
		),
	},
	{
		path: "/projects/:projectId/boqs/new",
		element: (
			<ProtectedRoute requireUserType>
				<BOQForm />
			</ProtectedRoute>
		),
	},
	{
		path: "/boqs/:boqId",
		element: (
			<ProtectedRoute requireUserType>
				<BOQDetail />
			</ProtectedRoute>
		),
	},
	{
		// A document to print or save as a PDF, without the app's chrome.
		path: "/boqs/:boqId/print",
		element: (
			<ProtectedRoute requireUserType hideAppShell>
				<BoqPrintPage />
			</ProtectedRoute>
		),
	},
	{
		path: "/proposals",
		element: (
			<ProtectedRoute requireUserType>
				<ProposalsPage />
			</ProtectedRoute>
		),
	},
	{
		path: "/requests",
		element: (
			<ProtectedRoute requireUserType="designer">
				<RequestsPage />
			</ProtectedRoute>
		),
	},
];
